import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { QueryConstraint } from "firebase/firestore";

import { COLLECTION_ID, Space } from "@highnote/server/src/core/entities";
import { highnote } from "@highnote/server/src/sdk";
import { useRecordPublicRoles } from "App/components/PublicRolesContext";
import { reportListenerError } from "App/modules/membershipTelemetry";

type WatcherAction =
  | {
      type: "ATTACH_LISTENER";
      key: string;
      watcher: () => void;
      componentId: WatcherComponentId;
    }
  | { type: "DETACH_LISTENER"; key: string; componentId: WatcherComponentId }
  | { type: "LISTENER_FAILED"; key: string; error: string }
  | {
      type: "SET_LOADING";
      key: string;
      componentId?: WatcherComponentId;
      loading: boolean;
    }
  | { type: "SET_ERROR"; key: string; error: string };

export type WatcherState = Map<
  string,
  {
    watcher?: () => void;
    loading?: Map<WatcherComponentId, boolean>;
    error?: string;
    components?: Set<string>;
  }
>;

// Reducer function for managing watchers
function watcherReducer(
  state: WatcherState,
  action: WatcherAction,
): WatcherState {
  switch (action.type) {
    case "ATTACH_LISTENER": {
      const newState = new Map(state);
      const existingWatcher = state.get(action.key);
      const isExistingComponent = existingWatcher?.components?.has(
        action.componentId,
      );

      if (existingWatcher) {
        if (isExistingComponent && existingWatcher.watcher) {
          // To avoid re-renders, use the existing state
          return state;
        }

        newState.set(action.key, {
          ...existingWatcher,
          // `manageWatcher` dispatches SET_LOADING before ATTACH_LISTENER,
          // so the entry already exists by the time the first listener's
          // unsubscribe arrives here. Keep an unsubscribe that is already
          // stored (a second component joining a live listener), else
          // store this one (the first listener, or a resubscribe after
          // LISTENER_FAILED cleared the dead one). Dropping it — the old
          // behaviour — meant DETACH_LISTENER never closed a listener.
          watcher: existingWatcher.watcher ?? action.watcher,
          // Add the new component to the set
          components: new Set([
            ...Array.from(existingWatcher.components || []),
            action.componentId,
          ]),
        });
      } else {
        // If the watcher does not exist, create a new one
        newState.set(action.key, {
          watcher: action.watcher,
          components: new Set([action.componentId]),
        });
      }
      return newState;
    }

    case "DETACH_LISTENER": {
      const newState = new Map(state);
      const existingWatcher = state.get(action.key);
      if (!existingWatcher) return state;

      const newComponents = new Set(existingWatcher.components);
      newComponents.delete(action.componentId);

      if (newComponents.size === 0) {
        // No components left: the entry goes. `manageWatcher` (which owns
        // the live listeners, in a ref) has already closed the listener.
        newState.delete(action.key);
      } else {
        // If there are still components left, update the watcher state
        // with the new components
        newState.set(action.key, {
          ...existingWatcher,
          components: newComponents,
        });
      }
      return newState;
    }

    case "SET_LOADING": {
      const existingWatcher = state.get(action.key);
      const currentLoading = existingWatcher?.loading?.get(action.componentId);

      // Skip update if loading state hasn't changed
      if (currentLoading === action.loading) return state;

      const newState = new Map(state);
      const newLoadingMap = new Map(existingWatcher?.loading);

      if (action.loading) {
        newLoadingMap.set(action.componentId, action.loading);
      } else {
        newLoadingMap.delete(action.componentId);
      }

      newState.set(
        action.key,
        existingWatcher
          ? {
              ...existingWatcher,
              loading: newLoadingMap,
            }
          : {
              loading: newLoadingMap,
            },
      );
      return newState;
    }

    case "LISTENER_FAILED": {
      // Firestore's onSnapshot error callback is terminal: the listener is
      // dead and already unsubscribed. Record the error, mark nothing as
      // loading, and drop the dead unsubscribe so a resubscribe can store
      // its own. The components stay — they are still interested.
      const existingWatcher = state.get(action.key);
      if (!existingWatcher) return state;
      const newState = new Map(state);
      newState.set(action.key, {
        ...existingWatcher,
        watcher: undefined,
        loading: new Map(),
        error: action.error,
      });
      return newState;
    }

    case "SET_ERROR": {
      const newState = new Map(state);
      const existingWatcher = state.get(action.key);

      newState.set(
        action.key,
        existingWatcher
          ? {
              ...existingWatcher,
              error: action.error,
            }
          : {
              error: action.error,
            },
      );
      return newState;
    }

    default:
      return state;
  }
}

export type WatcherActionType = "attach" | "detach";

// All our component ID types
type SpaceWatcherIds =
  | "space-home"
  | "space-home-child-item-rows"
  | "space-picker-all-spaces"
  | "space-picker-pinned-space"
  | "space-navigation"
  | "space-global-audio-player";

// TODO: Add moveToSpace + addToSpace dialog watcher ids
type SpaceDialogWatcherIds =
  | "share-dialog"
  | "click-thru-space-row"
  | "space-track-editor";

type SpaceBreadcrumbWatcherIds =
  | "breadcrumbs-space-home"
  | "breadcrumbs-space-track";

/**
 * Exported for `useRevocationTeardown`, which detaches space watchers when
 * the user loses access. This type was a private alias. The teardown derives
 * its component-id array from it through a mapped
 * `Record<SpaceRelatedWatcherIds, true>`, so the compiler, not a reviewer,
 * checks that a new space-related watcher id cannot appear here without a
 * matching entry in the teardown.
 */
export type SpaceRelatedWatcherIds =
  | SpaceWatcherIds
  | SpaceDialogWatcherIds
  | SpaceBreadcrumbWatcherIds;

type TrackWatcherIds = "track-navigation";
type TrackVersionEditorWatcherIds = "track-version-editor";
type TrackRelatedWatcherIds = TrackVersionEditorWatcherIds | TrackWatcherIds;

type LibraryBreadcrumbWatcherIds = "breadcrumbs-library";
type LibraryRelatedWatcherIds = LibraryBreadcrumbWatcherIds;

// TODO: Add 'TrackRelatedWatcherIds`
export type WatcherComponentId =
  | SpaceRelatedWatcherIds
  | LibraryRelatedWatcherIds
  | TrackRelatedWatcherIds;

// Apply to base config
type BaseWatcherConfig = {
  componentId: WatcherComponentId;
  collectionId: COLLECTION_ID;
};

// Attach configurations
type SingleEntityAttachConfig<EntityType> = BaseWatcherConfig & {
  watcherType: "single";
  action: "attach";
  entityId: string;
  onEntityChange: {
    add: (entity: EntityType) => void;
    remove: (entityId: string) => void;
  };
};

type MultipleEntitiesAttachConfig<EntityType> = BaseWatcherConfig & {
  watcherType: "multiple";
  action: "attach";
  watcherKey: string;
  constraints: QueryConstraint[];
  onEntitiesChange: (entities: EntityType[]) => void;
  limit?: number;
};

// Split detach configurations
type SingleEntityDetachConfig = {
  action: "detach";
  watcherType: "single";
  componentId: WatcherComponentId;
  entityId: string;
};

type MultipleEntitiesDetachConfig = {
  action: "detach";
  watcherType: "multiple";
  componentId: WatcherComponentId;
  watcherKey: string;
};

type WatcherConfig<EntityType> =
  | SingleEntityAttachConfig<EntityType>
  | MultipleEntitiesAttachConfig<EntityType>
  | SingleEntityDetachConfig
  | MultipleEntitiesDetachConfig;

/**
 * Watcher management: one Firestore listener per key, shared by every
 * component that attaches to that key, closed when the last one detaches.
 *
 * - Type-safe configurations using discriminated unions.
 * - Component-based tracking: `components` per key, `loading` per component.
 * - Error handling: a failed listener records `error` at once (the
 *   onSnapshot error callback fires exactly once, so there is no later
 *   signal), reports to telemetry, and — unless the failure was
 *   `permission-denied`, which the same query can never recover from —
 *   resubscribes after `RETRY_DELAY_MS`, up to `MAX_RETRIES` times per key.
 *   A successful snapshot clears the error and the retry budget.
 */
const RETRY_DELAY_MS = 5000;
const MAX_RETRIES = 3;

const isTerminalListenerError = (error: unknown): boolean =>
  (error as { code?: string } | undefined)?.code === "permission-denied";

const describeListenerError = (error: unknown): string => {
  const { code, message } = (error ?? {}) as {
    code?: string;
    message?: string;
  };
  return `Error loading entity: ${code || message || "unknown"}`;
};

/**
 * The componentId `useSpace.tsx` attaches a space's child-entity watchers
 * (space + track collections) under, and the id `SpaceItems`/`SpaceTrack`
 * read `loading`/`error` for. Shared so the three cannot drift apart.
 */
export const SPACE_TRACKS_WATCHER_COMPONENT_ID: WatcherComponentId =
  "space-home";
export const useWatcherManagementV2 = <EntityType,>() => {
  /*
   * The write half of `PublicRolesContext`. The provider creates this value
   * once and it never changes. So a record call from inside `onSnapshot` on
   * this hot path can never re-render this hook's consumer, and a closure
   * over it inside `manageWatcher` does not need to widen that callback's
   * dependency array.
   */
  const recordPublicRoles = useRecordPublicRoles();
  const [totalLimit, setTotalLimit] = useState<number>(0);
  const [requestedLimit, setRequestedLimit] = useState<number>(0);
  const unmountedRef = useRef<boolean>(false);

  const [activeWatchers, setActiveWatchers] = useReducer(
    watcherReducer,
    new Map(),
  );

  useEffect(() => {
    return () => {
      // This is used to prevent the watcher from being called after the component has unmounted
      unmountedRef.current = true;
    };
  }, []);

  /*
   * The live listeners, per KEY, in a ref — the synchronous truth about
   * what is subscribed. `activeWatchers` (reducer state) is what the UI
   * renders from and lags a dispatch by a render, so it cannot be what
   * decides "is there already a listener for this key?": two components
   * attaching in the same commit would both see none and open two
   * Firestore listeners. Retry bookkeeping lives here too, not in
   * per-call locals: a `detach` is a different `manageWatcher` call from
   * the `attach` whose timer it must cancel.
   */
  const listenersRef = useRef<
    Map<
      string,
      {
        unsubscribe?: () => void;
        components: Set<WatcherComponentId>;
        retryTimer?: number;
        retries: number;
      }
    >
  >(new Map());

  const clearRetry = useCallback((watcherKey: string) => {
    const live = listenersRef.current.get(watcherKey);
    if (live?.retryTimer !== undefined) {
      clearTimeout(live.retryTimer);
      live.retryTimer = undefined;
    }
  }, []);

  const manageWatcher = useCallback(
    async (config: WatcherConfig<EntityType>) => {
      // Get the appropriate watcherKey based on config type
      const watcherKey =
        config.watcherType === "single" ? config.entityId : config.watcherKey;

      if (config.action === "detach") {
        const live = listenersRef.current.get(watcherKey);
        if (live) {
          live.components.delete(config.componentId);
          if (live.components.size === 0) {
            clearRetry(watcherKey);
            live.unsubscribe?.();
            listenersRef.current.delete(watcherKey);
          }
        }
        setActiveWatchers({
          type: "DETACH_LISTENER",
          key: watcherKey,
          componentId: config.componentId,
        });
        return;
      }

      const existing = listenersRef.current.get(watcherKey);

      // If this component is already watching, do nothing
      if (existing?.components.has(config.componentId)) {
        return;
      }

      // If we have a live listener, just add the new component
      if (existing?.unsubscribe) {
        existing.components.add(config.componentId);
        setActiveWatchers({
          type: "ATTACH_LISTENER",
          key: watcherKey,
          watcher: existing.unsubscribe,
          componentId: config.componentId,
        });
        return;
      }

      const live = existing ?? { components: new Set(), retries: 0 };
      live.components.add(config.componentId);
      listenersRef.current.set(watcherKey, live);

      const onSnapshotOk = () => {
        clearRetry(watcherKey);
        live.retries = 0;
        resetWatcher(watcherKey, config.componentId);
      };

      /*
       * The one failure path for both listener shapes. Records the error
       * immediately (there will be no second callback), reports it, and
       * either gives up (`permission-denied`, or budget spent — `onGiveUp`
       * lets the single-entity shape drop the entity from its store) or
       * schedules a resubscribe. The timer is skipped if every component
       * detached in the meantime: the key is then gone from state.
       */
      const failWatcher = (
        error: unknown,
        resubscribe: () => void,
        onGiveUp?: () => void,
      ) => {
        if (unmountedRef.current) return;
        live.unsubscribe = undefined;
        reportListenerError(config.collectionId, error as never);
        setActiveWatchers({
          type: "LISTENER_FAILED",
          key: watcherKey,
          error: describeListenerError(error),
        });

        // The budget stays spent until a snapshot succeeds or the key is
        // detached — a give-up is final for this subscription.
        if (isTerminalListenerError(error) || live.retries >= MAX_RETRIES) {
          onGiveUp?.();
          return;
        }
        live.retries += 1;
        clearRetry(watcherKey);
        live.retryTimer = window.setTimeout(() => {
          live.retryTimer = undefined;
          if (unmountedRef.current) return;
          // every component detached in the meantime
          if (listenersRef.current.get(watcherKey) !== live) return;
          resubscribe();
        }, RETRY_DELAY_MS);
      };

      // Only create a new Firebase connection if no live listener exists
      setActiveWatchers({
        type: "SET_LOADING",
        key: watcherKey,
        componentId: config.componentId,
        loading: true,
      });

      if (config.watcherType === "single") {
        const subscribe = () => {
          const unsubscribe = highnote.watchEntity({
            entityId: config.entityId,
            collectionId: config.collectionId,
            onChange: (entity: EntityType) => {
              if (unmountedRef.current) return;
              if (config.collectionId === COLLECTION_ID.SPACE) {
                recordPublicRoles(entity as unknown as Space);
              }
              config.onEntityChange.add(entity);
              onSnapshotOk();
            },
            onError: (error) => {
              unsubscribe();
              failWatcher(error, subscribe, () =>
                config.onEntityChange.remove(config.entityId),
              );
            },
          });
          live.unsubscribe = unsubscribe;

          setActiveWatchers({
            type: "ATTACH_LISTENER",
            key: watcherKey,
            watcher: unsubscribe,
            componentId: config.componentId,
          });
        };
        subscribe();
      }
      // Handle multiple entities watch
      else if (config.watcherType === "multiple") {
        if (config.limit) {
          setRequestedLimit(config.limit);
        }

        const subscribe = () => {
          const unsubscribe = highnote.watchEntities({
            collectionId: config.collectionId,
            constraints: config.constraints,
            onChange: (entities: EntityType[]) => {
              if (unmountedRef.current) return;
              if (config.collectionId === COLLECTION_ID.SPACE) {
                entities.forEach((entity) =>
                  recordPublicRoles(entity as unknown as Space),
                );
              }
              config.onEntitiesChange(entities);
              onSnapshotOk();
            },
            /*
             * `error` is the `FirestoreError`: the difference between
             * `permission-denied` and `failed-precondition`. This is the
             * folder-expansion query; before the error was recorded, a
             * denial here rendered as an empty folder, silently, forever.
             */
            onError: (error) => {
              unsubscribe();
              failWatcher(error, subscribe);
            },
          });
          live.unsubscribe = unsubscribe;

          setActiveWatchers({
            type: "ATTACH_LISTENER",
            key: watcherKey,
            watcher: unsubscribe,
            componentId: config.componentId,
          });
        };
        subscribe();
      }
    },
    [totalLimit],
  );

  const resetWatcher = useCallback(
    (watcherKey: string, componentId: WatcherComponentId) => {
      setActiveWatchers({
        type: "SET_ERROR",
        key: watcherKey,
        error: "",
      });
      setActiveWatchers({
        type: "SET_LOADING",
        key: watcherKey,
        componentId,
        loading: false,
      });
    },
    [],
  );

  useEffect(() => {
    if (requestedLimit) {
      // Ability to set the totalLimit via the manageEnititiesWatchers args
      // We use requestedLimit to set the initial totalLimit.
      setTotalLimit((prev) => prev + requestedLimit);
    }
  }, [requestedLimit]);

  return {
    activeWatchers,
    manageWatcher,
    // TODO: (GLOBAL SPACE): This is not used now, but may be used in the future.
    // Reconsider current implementation, and maybe adding into each watcher object.
    loadMore: () => setTotalLimit((prev) => prev + requestedLimit),
  };
};
